<script setup lang="ts">
import Icon from '../icon/Icon.vue';
import type { IconName } from '../icon/Icon.types';

export interface OverviewMetric {
	id: string;
	icon: IconName;
	label: string;
	status: string;
	value: string | number;
	/** Visual tone for status + value */
	tone?: 'neutral' | 'alert';
	trend?: 'up' | 'down' | 'none';
	/** Optional app target for row navigation */
	appId?: string;
}

export interface OverviewActivityProps {
	title?: string;
	subtitle?: string;
	logoSrc?: string;
	logoAlt?: string;
	metrics?: OverviewMetric[];
	updatedLabel?: string;
	actionLabel?: string;
	customClass?: string;
}

withDefaults(defineProps<OverviewActivityProps>(), {
	title: 'Overview',
	subtitle: 'Your activity at a glance',
	logoAlt: 'Apexx',
	metrics: () => [],
	updatedLabel: 'Updated just now',
	actionLabel: 'Open activity',
	customClass: '',
});

const emit = defineEmits<{
	action: [];
	select: [metric: OverviewMetric];
}>();

function toneClass(tone: OverviewMetric['tone'] = 'neutral'): string {
	return tone === 'alert' ? 'text-critical' : 'text-on-surface';
}

function statusClass(tone: OverviewMetric['tone'] = 'neutral'): string {
	return tone === 'alert' ? 'text-critical' : 'text-on-surface-muted';
}
</script>

<template>
	<section :class="['flex w-full flex-col gap-4', customClass]">
		<header class="flex items-center gap-3">
			<div
				v-if="logoSrc"
				class="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-white shadow-sm"
			>
				<img :src="logoSrc" :alt="logoAlt" class="h-full w-full object-contain p-1.5" />
			</div>
			<div class="min-w-0">
				<h2 class="text-xl font-semibold tracking-tight text-on-surface">
					{{ title }}
				</h2>
				<p v-if="subtitle" class="mt-0.5 text-sm text-on-surface-muted">
					{{ subtitle }}
				</p>
			</div>
		</header>

		<div class="overflow-hidden rounded-3xl bg-white shadow-sm">
			<ul class="divide-y divide-slate-100" role="list">
				<li
					v-for="metric in metrics"
					:key="metric.id"
					class="flex cursor-pointer items-center gap-3 px-4 py-4 transition-colors hover:bg-slate-50 sm:gap-4 sm:px-5"
					@click="emit('select', metric)"
				>
					<div
						class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600"
					>
						<Icon :name="metric.icon" size="sm" />
					</div>

					<div class="min-w-0 flex-1">
						<p class="truncate text-sm font-semibold text-on-surface">
							{{ metric.label }}
						</p>
						<p
							:class="['mt-0.5 truncate text-xs', statusClass(metric.tone)]"
						>
							{{ metric.status }}
						</p>
					</div>

					<div
						:class="[
							'flex shrink-0 items-center gap-1 text-2xl font-semibold tabular-nums',
							toneClass(metric.tone),
						]"
					>
						<span>{{ metric.value }}</span>
						<Icon
							v-if="metric.trend === 'up'"
							name="trending_up"
							size="sm"
							:class="toneClass(metric.tone)"
						/>
						<Icon
							v-else-if="metric.trend === 'down'"
							name="trending_down"
							size="sm"
							:class="toneClass(metric.tone)"
						/>
					</div>
				</li>
			</ul>
		</div>

		<footer class="flex items-center justify-between gap-3 px-0.5">
			<p class="text-xs text-on-surface-muted">{{ updatedLabel }}</p>
			<!-- <button
				v-if="actionLabel"
				type="button"
				class="inline-flex items-center gap-1 text-sm font-semibold text-critical transition hover:opacity-80"
				@click="emit('action')"
			>
				{{ actionLabel }}
				<span aria-hidden="true">→</span>
			</button> -->
		</footer>
	</section>
</template>
